import { FirebaseApp, initializeApp, FirebaseError } from 'firebase/app';

import { FirebaseStorage, getStorage } from 'firebase/storage';
import {
    onAuthStateChanged,
    onIdTokenChanged,
    signOut,
    signInWithCustomToken,
    NextOrObserver,
    User,
    Auth,
    setPersistence,
    indexedDBLocalPersistence,
    initializeAuth,
    browserLocalPersistence,
} from 'firebase/auth';

import { AppDispatch } from '@/store/store'
import { getFirebaseConfig } from './firebase-config';
import NetworkStatusService from '@/shared/services/NetworkStatusService'
import { idbSet, idbRemove } from '@/shared/IndexedDBHelper'


export namespace FirebaseWrapper {
    export type EventCallback = (...args: any[]) => void;

    export enum Events {
        Login = "login",
        Logout = "logout"
    }
    
    export class FirebaseBaseWrapper {
        firebaseAuth: Auth
        storage: FirebaseStorage
        app: FirebaseApp;

        private signInRetryAttempts = 0;
        private hasLoggedNetworkError = false;
        private signInRetryTimer?: ReturnType<typeof setTimeout>;
        private signInRetryListener?: (online: boolean) => void;
        private authSession: object | null = null;
        private authMutation: Promise<void> = Promise.resolve();

        constructor() {
            this.app = initializeApp(getFirebaseConfig());
            this.firebaseAuth = initializeAuth(this.app, {
                persistence: [indexedDBLocalPersistence, browserLocalPersistence]
            });

            // Keep your cached token in sync
            onIdTokenChanged(this.firebaseAuth, async (user) => {
                if (user) {
                    try {
                        const idToken = await user.getIdToken(); // no 'true' here; SDK refreshes automatically
                        this.emit(FirebaseWrapper.Events.Login, idToken, user.uid);
                    } catch (e) {
                        console.error('[SW] Failed to cache refreshed id token.', e);
                    }
                }
            });

            // (Optional) mirror login/logout events
            onAuthStateChanged(this.firebaseAuth, (user) => {
                // if (user) {
                //     this.emit(FirebaseWrapper.Events.Login, user.uid);
                // }
                // else {
                //     this.emit(FirebaseWrapper.Events.Logout);
                // }
            });

            this.storage = getStorage(this.app);
            this.storage.maxUploadRetryTime = 30000; // 30,000 milliseconds = 30 seconds. though this doesn't seem to have any effect =\
        }
    
        private static _shared: FirebaseBaseWrapper;
        static get shared(): FirebaseBaseWrapper {
            if (!this._shared) {
                this._shared = new FirebaseBaseWrapper();
            }
            return this._shared;
        }
    
        private enqueueAuthMutation<T>(operation: () => Promise<T>): Promise<T> {
            const pending = this.authMutation.then(operation);
            this.authMutation = pending.then(() => undefined, () => undefined);
            return pending;
        }

        private clearSignInRetry(): void {
            if (this.signInRetryTimer) clearTimeout(this.signInRetryTimer);
            this.signInRetryTimer = undefined;
            if (this.signInRetryListener) {
                NetworkStatusService.getInstance().removeListener(this.signInRetryListener);
                this.signInRetryListener = undefined;
            }
        }

        signInUser = (token: string) => {
            const session = {};
            this.authSession = session;
            this.clearSignInRetry();
            return this.enqueueAuthMutation(() => this.signIn(token, session));
        }

        private async signIn(token: string, session: object) {
            if (!token || this.authSession !== session) return null;
            const service = NetworkStatusService.getInstance();
            const isOnline = await service.getLatestStatusConnection();
            if (this.authSession !== session) return null;
            if (!isOnline) {
                if (!this.hasLoggedNetworkError) {
                    console.warn('[SW] Cannot sign in: no internet connection.');
                    this.hasLoggedNetworkError = true;
                }
                const retry = async (online: boolean) => {
                    if (online && this.authSession === session) {
                        service.removeListener(retry);
                        this.hasLoggedNetworkError = false;
                        this.signInRetryAttempts = 0;
                        await this.enqueueAuthMutation(() => this.signIn(token, session));
                    }
                };
                this.signInRetryListener = retry;
                service.addListener(retry);
                return null;
            }
            try {
                try {
                    const userCredential = await signInWithCustomToken(this.firebaseAuth, token)
                    if (this.authSession !== session) return null;
                    const idToken = await userCredential.user.getIdToken()
                    if (this.authSession !== session) return null;
                    this.emit(Events.Login, idToken, userCredential.user.uid)
                    this.signInRetryAttempts = 0
                    this.hasLoggedNetworkError = false
                    return userCredential
                } catch (e) {
                    if (this.authSession !== session) return null;
                    console.error('[SW] Failed to cache id token.', e)
                }
            } catch (error) {
                if (this.authSession !== session) return null;
                const err = error as FirebaseError & { code?: string };
                if (err?.code === 'auth/network-request-failed') {
                    const online = await service.getLatestStatusConnection();
                    if (this.authSession !== session) return null;
                    if (!online) {
                        if (!this.hasLoggedNetworkError) {
                            console.warn('[SW] Sign-in failed: offline. Waiting for connection to retry.');
                            this.hasLoggedNetworkError = true;
                        }
                        const retry = async (isOnline: boolean) => {
                            if (isOnline && this.authSession === session) {
                                service.removeListener(retry);
                                this.hasLoggedNetworkError = false;
                                this.signInRetryAttempts = 0;
                                await this.enqueueAuthMutation(() => this.signIn(token, session));
                            }
                        };
                        this.signInRetryListener = retry;
                        service.addListener(retry);
                    } else {
                        const delay = Math.min(30000, 1000 * Math.pow(2, this.signInRetryAttempts));
                        this.signInRetryAttempts++;
                        if (!this.hasLoggedNetworkError) {
                            console.warn(`[SW] Network request failed during sign-in. Retrying in ${delay}ms.`);
                            this.hasLoggedNetworkError = true;
                        } else {
                            console.debug(`[SW] Network request failed during sign-in. Retrying in ${delay}ms.`);
                        }
                        if (this.signInRetryTimer) clearTimeout(this.signInRetryTimer);
                        this.signInRetryTimer = setTimeout(() => {
                            if (this.authSession === session) {
                                void this.enqueueAuthMutation(() => this.signIn(token, session));
                            }
                        }, delay);
                    }
                } else {
                    console.error('[SW] Failed to signin with custom token.', error);
                }
                return null;
            }

        }
        
        signOutUser = () => {
            this.authSession = null;
            this.clearSignInRetry();
            return this.enqueueAuthMutation(async () => {
                try {
                    await signOut(this.firebaseAuth);
                    await (this.firebaseAuth as any).authStateReady?.();
                    this.emit(Events.Logout);
                } catch (error) {
                    console.error("Error signing out:", error);
                    throw error;
                }
            });
        }

        listenToAuthChanges = (dispatch: AppDispatch) => (user: User | null) => {
            if (user) {
                this.emit(Events.Login, user.uid)
            }
            // this is calld to often unfortunately even for temp unstable states 
            //  else {
            //     dispatch(fblogout());
            //     this.emit(Events.Logout)
            // }
        };
    
        private subscribers: Map<string, EventCallback[]> = new Map();
    
        subscribe(event: Events, callback: EventCallback): void {
            if (!this.subscribers.has(event)) {
                this.subscribers.set(event, []);
            }
            this.subscribers.get(event)?.push(callback);
        }
    
        unsubscribe(event: Events, callback: EventCallback): void {
            const callbacks = this.subscribers.get(event);
            if (!callbacks) return;
    
            const index = callbacks.indexOf(callback);
            if (index !== -1) {
                callbacks.splice(index, 1);
            }
    
            // Clean up if no more callbacks are left for the event
            if (callbacks.length === 0) {
                this.subscribers.delete(event);
            }
        }
    
        emit(event: Events, ...args: any[]): void {
            const callbacks = this.subscribers.get(event);
            if (callbacks) {
                callbacks.forEach(callback => callback(...args));
            }
        }
    }
}


//required if you want to keep logged in after user exits the browser or closes tab
// setPersistence(firebaseAuth, browserLocalPersistence);
